import type { Order, OrderStatus, Restaurant, StaffProfile } from "./types";

export type AttentionLevel = "normal" | "watch" | "late";

export interface OrderFilters {
  query: string;
  restaurantId: string;
  driverId: string;
}

const attentionThresholds: Record<OrderStatus, number> = {
  pending_merchant: 5,
  preparing: 25,
  ready_for_pickup: 8,
  on_the_way: 35,
  delivered: Number.POSITIVE_INFINITY,
  cancelled: Number.POSITIVE_INFINITY,
};

export function normalizeOperationalSearch(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u064B-\u065F\u0670]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .trim()
    .toLocaleLowerCase("ar");
}

export function minutesSince(value: string, now = Date.now()): number {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return 0;
  return Math.max(0, Math.floor((now - timestamp) / 60_000));
}

export function getOrderAttention(order: Order, now = Date.now()): {
  level: AttentionLevel;
  minutes: number;
  threshold: number;
} {
  const minutes = minutesSince(order.updatedAt, now);
  const threshold = order.status === "preparing" && order.prepMinutes
    ? Math.max(10, order.prepMinutes)
    : attentionThresholds[order.status];

  if (!Number.isFinite(threshold)) return { level: "normal", minutes, threshold };
  if (minutes >= threshold) return { level: "late", minutes, threshold };
  if (minutes >= Math.max(3, Math.floor(threshold * 0.7))) {
    return { level: "watch", minutes, threshold };
  }
  return { level: "normal", minutes, threshold };
}

export function filterOperationalOrders(
  orders: Order[],
  restaurants: Restaurant[],
  staff: StaffProfile[],
  filters: OrderFilters,
): Order[] {
  const query = normalizeOperationalSearch(filters.query);
  const restaurantNames = new Map(
    restaurants.map((restaurant) => [restaurant.id, restaurant.name]),
  );
  const staffNames = new Map(
    staff.map((profile) => [profile.subjectId, profile.displayName]),
  );

  return orders
    .filter((order) => {
      if (filters.restaurantId && order.restaurantId !== filters.restaurantId) return false;
      if (filters.driverId === "unassigned" && order.driverId) return false;
      if (
        filters.driverId &&
        filters.driverId !== "unassigned" &&
        order.driverId !== filters.driverId
      ) return false;
      if (!query) return true;

      const searchable = normalizeOperationalSearch([
        order.id,
        order.id.slice(-7),
        restaurantNames.get(order.restaurantId) ?? "",
        order.customerName ?? "",
        order.address.area,
        order.address.landmark,
        order.address.phone,
        order.backupPhone ?? "",
        order.driverId ?? "",
        order.driverId ? staffNames.get(order.driverId) ?? "" : "",
        ...order.items.map((item) => item.name),
      ].join(" "));
      return query.split(/\s+/).every((term) => searchable.includes(term));
    })
    .sort((first, second) =>
      Date.parse(first.updatedAt) - Date.parse(second.updatedAt),
    );
}
